import { Button, Popconfirm, Space, Tooltip } from "antd";
import {
  Check,
  CircleCheckBig,
  NotebookPen,
  Pause,
  Play,
  Undo2,
  UserX,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ExamPeriod,
  ExamPeriodMonitor,
  ExamPeriodMonitorState,
  ExamPeriodState,
} from "@types";
import { useEditApplicantMonitor, useSignalRConnection } from "@hooks";
import { motion } from "framer-motion";
import { getKeycloakRealm } from "../helpers";

interface Props {
  examPeriodActivity: ExamPeriodMonitor;
  examPeriod?: ExamPeriod;
}

export const ExamPeriodActivityTableActions = ({
  examPeriodActivity,
  examPeriod,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editApplicantMonitor = useEditApplicantMonitor(examPeriod);

  const {
    sendPauseSingleParticipantsExamInstance,
    sendContinueSingle,
    sendDisqualifySingle,
    sendNoShowSingle,
    sendCheckoutSingle,
    sendRegisterSingle,
  } = useSignalRConnection();
  const iconDisabled =
    examPeriod?.state === ExamPeriodState.Completed ||
    examPeriodActivity.state === ExamPeriodMonitorState.Completed
      ? true
      : false;

  const displayButtonsByState = () => {
    switch (examPeriodActivity.state) {
      case ExamPeriodMonitorState.WaitingForParticipant:
        return (
          <motion.div
            key={1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip
                title={t("pages.examPeriods.registerApplicant")}
                placement="left"
              >
                <Button
                  disabled={iconDisabled}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: 2,
                      },
                      {
                        onSuccess: () =>
                          sendRegisterSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "register-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                  icon={<Check />}
                />
              </Tooltip>
              <Tooltip
                title={t("pages.examPeriods.applicantNotCome")}
                placement="right"
              >
                <Button
                  disabled={iconDisabled}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: 3,
                      },
                      {
                        onSuccess: () =>
                          sendNoShowSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "noShow-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                  icon={<X />}
                />
              </Tooltip>
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.ParticipantConfirmed:
        return (
          <motion.div
            key={2}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip
                title={t("pages.examPeriods.checkoutApplicant")}
                placement="left"
              >
                <Button
                  disabled={iconDisabled}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: 1,
                      },
                      {
                        onSuccess: () =>
                          sendCheckoutSingle(
                            examPeriodActivity.userExaminee.externalId!,
                            "checkout-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                  icon={<CircleCheckBig style={{ color: "green" }} />}
                />
              </Tooltip>
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.NoShow:
        return (
          <motion.div
            key={3}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Tooltip
              title={t("pages.examPeriods.applicantCome")}
              placement="left"
            >
              <Button
                type="dashed"
                disabled={iconDisabled}
                icon={<Undo2 />}
                loading={editApplicantMonitor.isPending}
                onClick={() =>
                  editApplicantMonitor.mutate({
                    id: examPeriodActivity.id,
                    state: 1,
                  })
                }
              />
            </Tooltip>
          </motion.div>
        );
      case ExamPeriodMonitorState.Completed:
      case ExamPeriodMonitorState.Excluded:
        return (
          <motion.div
            key={4}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Tooltip title={t("pages.examPeriods.gradeExam")} placement="right">
              <Button
                type="dashed"
                icon={<NotebookPen />}
                onClick={() =>
                  navigate(`/${getKeycloakRealm}/exam-periods/correction/${examPeriodActivity.id}`)
                }
              />
            </Tooltip>
          </motion.div>
        );
      case ExamPeriodMonitorState.InProgress:
        return (
          <motion.div
            key={5}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Space>
              <Tooltip title={t("pages.examPeriods.pause")} placement="left">
                <Button
                  type="dashed"
                  icon={<Pause />}
                  loading={editApplicantMonitor.isPending}
                  onClick={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: ExamPeriodMonitorState.Paused,
                      },
                      {
                        onSuccess: () =>
                          sendPauseSingleParticipantsExamInstance(
                            examPeriodActivity.userExaminee.externalId!,
                            "pause-" + examPeriodActivity.id
                          ),
                      }
                    );
                  }}
                />
              </Tooltip>
              <Tooltip
                title={t("pages.examPeriods.disqualified")}
                placement="right"
              >
                <Popconfirm
                  title={t("pages.examPeriods.disqualifiedConfirm")}
                  okButtonProps={{ loading: editApplicantMonitor.isPending }}
                  onConfirm={() => {
                    editApplicantMonitor.mutate(
                      {
                        id: examPeriodActivity.id,
                        state: ExamPeriodMonitorState.Excluded,
                      },
                      {
                        onSuccess: () =>
                          sendDisqualifySingle(
                            examPeriodActivity.userExaminee.externalId,
                            "disqualify-" + examPeriodActivity?.id
                          ),
                      }
                    );
                  }}
                >
                  <Button type="primary" danger icon={<UserX />} />
                </Popconfirm>
              </Tooltip>
            </Space>
          </motion.div>
        );
      case ExamPeriodMonitorState.Paused:
        return (
          <motion.div
            key={6}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
          >
            <Tooltip title={t("pages.examPeriods.continue")} placement="left">
              <Button
                type="dashed"
                icon={<Play />}
                loading={editApplicantMonitor.isPending}
                onClick={() => {
                  editApplicantMonitor.mutate(
                    {
                      id: examPeriodActivity.id,
                      state: ExamPeriodMonitorState.InProgress,
                    },
                    {
                      onSuccess: () =>
                        sendContinueSingle(
                          examPeriodActivity.userExaminee.externalId!,
                          "continue-" + examPeriodActivity.id
                        ),
                    }
                  );
                }}
              />
            </Tooltip>
          </motion.div>
        );
      default:
        return <></>;
    }
  };
  return <div>{displayButtonsByState()}</div>;
};
